/**
 * The decisions behind an interactive page inline in chat
 * (`WorkspaceHtmlPage.tsx`), kept free of React so they can be unit tested:
 * when to ask the server for the page's URL, how long to keep retrying while
 * the turn's workspace sync lands, and which messages from the page frame the
 * host acts on.
 */
import {
  clampHtmlPageHeight,
  readHtmlPageContentHeight,
  readHtmlPageLinkRequest,
} from "@amoreg/shared/htmlPage";

/**
 * Backoff after the reply finishes streaming. The page is written in the
 * agent's sandbox and reaches the server when the turn settles, so the first
 * URL requests can find no file yet; these cover a slow sync (~42 s in all).
 */
export const HTML_PAGE_RETRY_DELAYS_MS: ReadonlyArray<number> = [
  1_500, 3_000, 6_000, 12_000, 20_000,
];
/** Backoff for a page in a reply that was already finished when it mounted. */
export const HTML_PAGE_SETTLED_RETRY_DELAYS_MS: ReadonlyArray<number> = [1_000, 3_000];

export type HtmlPageLoad =
  /** The reply is still streaming: the page cannot exist on the server yet. */
  | { readonly phase: "waiting"; readonly sawStreaming: true }
  | { readonly phase: "minting"; readonly attempt: number; readonly sawStreaming: boolean }
  | {
      readonly phase: "retrying";
      readonly attempt: number;
      readonly delayMs: number;
      readonly sawStreaming: boolean;
    }
  | { readonly phase: "ready"; readonly url: string }
  | { readonly phase: "unavailable"; readonly sawStreaming: boolean };

export type HtmlPageLoadEvent =
  | { readonly type: "streaming"; readonly streaming: boolean }
  | { readonly type: "minted"; readonly url: string }
  | { readonly type: "failed" }
  | { readonly type: "retry-due" }
  | { readonly type: "retry" };

export function initialHtmlPageLoad(streaming: boolean): HtmlPageLoad {
  return streaming
    ? { phase: "waiting", sawStreaming: true }
    : { phase: "minting", attempt: 0, sawStreaming: false };
}

/** Whether the frame area should say the page is on its way. */
export function isHtmlPagePreparing(load: HtmlPageLoad): boolean {
  return load.phase === "waiting" || load.phase === "minting" || load.phase === "retrying";
}

export function nextHtmlPageLoad(load: HtmlPageLoad, event: HtmlPageLoadEvent): HtmlPageLoad {
  switch (event.type) {
    case "streaming":
      if (event.streaming) {
        return load.phase === "ready" ? load : { phase: "waiting", sawStreaming: true };
      }
      return load.phase === "waiting" ? { phase: "minting", attempt: 0, sawStreaming: true } : load;
    case "minted":
      return load.phase === "minting" ? { phase: "ready", url: event.url } : load;
    case "failed": {
      if (load.phase !== "minting") return load;
      const delays = load.sawStreaming
        ? HTML_PAGE_RETRY_DELAYS_MS
        : HTML_PAGE_SETTLED_RETRY_DELAYS_MS;
      const delayMs = delays[load.attempt];
      return delayMs === undefined
        ? { phase: "unavailable", sawStreaming: load.sawStreaming }
        : { phase: "retrying", attempt: load.attempt, delayMs, sawStreaming: load.sawStreaming };
    }
    case "retry-due":
      return load.phase === "retrying"
        ? { phase: "minting", attempt: load.attempt + 1, sawStreaming: load.sawStreaming }
        : load;
    case "retry":
      // The reader's "Try again": one more round with the short backoff.
      return load.phase === "unavailable"
        ? { phase: "minting", attempt: 0, sawStreaming: false }
        : load;
  }
}

export type HtmlPageFrameAction =
  | { readonly kind: "height"; readonly height: number }
  | { readonly kind: "open-link"; readonly id: string | number; readonly url: string };

/**
 * What a message from the page frame asks of the host, if anything. Only the
 * frame's own window is listened to (any other frame or window could post the
 * same shapes). A link opens only while the frame has focus and the reader has
 * just interacted with the page, so a page cannot open tabs on load or on a
 * timer; and only http(s) URLs (`readHtmlPageLinkRequest`).
 */
export function readHtmlPageFrameMessage(input: {
  readonly data: unknown;
  readonly source: unknown;
  readonly frameWindow: unknown;
  readonly frameFocused: boolean;
  readonly userActive: boolean;
}): HtmlPageFrameAction | null {
  if (input.frameWindow == null || input.source !== input.frameWindow) return null;
  const height = readHtmlPageContentHeight(input.data);
  if (height !== undefined) return { kind: "height", height: clampHtmlPageHeight(height) };
  const link = readHtmlPageLinkRequest(input.data);
  if (link !== undefined && input.frameFocused && input.userActive) {
    return { kind: "open-link", id: link.id, url: link.url };
  }
  return null;
}

/** A stable floating-window id for one page of one thread. */
export function floatingHtmlPageId(threadKey: string, path: string): string {
  return `page:${threadKey}:${path}`;
}

/** A readable title for a page: the markdown alt text, else its file name. */
export function htmlPageTitle(alt: string | undefined, path: string): string {
  const trimmed = alt?.trim();
  if (trimmed) return trimmed;
  return path.split(/[\\/]/).pop() || "Page";
}
